/*
  Dynamic stock status — PDP availability dropdown + Add-to-Cart label.
  Paired with snippets/dynamic-stock.liquid (emits #dyn-stock-data) + dynamic-stock.css.
  The snippet only emits data for `track-stock` products in the US/CA markets, so if this
  file runs at all, custom per-variant dates, status messages and ATC labels always apply.

  Per the currently-selected variant (variant.available / custom.in_stock / custom.shipping_days):
    • available === false → grey dot  + unavailable copy + ATC disabled, label untouched
    • in_stock === true   → green dot + arrival date + ATC "Add to Cart"
    • else                → amber dot + production-run ship date + ATC pre-order label
  Shopify availability is checked FIRST: a sold-out variant is rejected by /cart/add with a
  422, so offering a pre-order there is a dead end regardless of what the metafields say.
  <date> = the shipping_days-th business day counting from today (today = day 1, Sat/Sun
  skipped), formatted "July 20".

  A/B TEST (Intelligems, one theme + injected custom code — see
  .claude/features/dynamic-stock-status/OUTPUT-ab-test-plan.md):
    • group `control`  — what is live today: recolour GemPages' own dot, swap its
                         estimate-date copy, leave its row and pink pill in place.
    • group `dropdown` — the collapsible row; GemPages' row and pill hidden.
  Both groups share the data, date maths and ATC-label logic; only presentation differs.
  Group is owned by assets/pdp-ab-group.js (shared with the image-labels feature); this
  file only reads it. Re-resolved on every tick and via its onChange, so a late Intelligems
  assignment swaps renderers cleanly through teardown().

  Runs on both surfaces:
    • GemPages — paints into / replaces the existing dot/date row, rewrites the ATC label.
    • Native   — injects the message or dropdown above button.m-add-to-cart.

  Page-level orchestrator (not a web component) because it coordinates elements that live
  across GemPages/native markup it does not own, and must survive their re-renders.
*/
(function () {
  if (window.__dynStockBound) return;
  window.__dynStockBound = true;

  const dataEl = document.getElementById("dyn-stock-data");
  if (!dataEl) return;

  let DATA;
  try {
    DATA = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }
  const VARIANTS = DATA.variants || {};
  // The `track-stock` tag + US/CA market gate the DATA only, never the presentation.
  // False → the dropdown still renders, but with fallback copy and no ATC-label rewriting.
  const DATA_ENABLED = DATA.dataEnabled === true;
  const CFG = DATA.config || {};

  /* A product template can carry a "Stock dropdown" block, whose settings are this
     page's own wording. They win over the locale defaults the snippet emitted.
     A blank setting is skipped rather than applied, so a field the merchant left
     alone keeps the sitewide copy instead of blanking the row. */
  (function applyBlockConfig() {
    const node = document.getElementById("dyn-stock-config-override");
    if (!node) return;
    let override;
    try {
      override = JSON.parse(node.textContent);
    } catch (err) {
      return;
    }
    Object.keys(override || {}).forEach((key) => {
      const value = override[key];
      if (value !== null && value !== "") CFG[key] = value;
    });
  })();

  /* ---------- A/B group ---------- */
  // The dropdown finished testing and is now the experience for everyone, so this
  // feature no longer asks which arm the shopper is in. The shared resolver in
  // assets/pdp-ab-group.js is deliberately left alone: the image labels and the
  // rating badge still run off it, and flipping its default would ship those two
  // as well.
  //
  // The control renderer below is kept rather than deleted — putting this back
  // behind the test is then a one-line change here, not a rebuild.
  const CONTROL = "control";
  const DROPDOWN = "dropdown";

  // Set to false to hand presentation back to the A/B group.
  const ALWAYS_DROPDOWN = true;

  function resolveGroup() {
    if (ALWAYS_DROPDOWN) return DROPDOWN;
    return window.pdpAb ? window.pdpAb.refresh() : CONTROL;
  }

  let group = resolveGroup();

  let expected = null; // stock state currently applied
  let lastVid = null;
  let observer = null;
  let tickScheduled = false;

  /* ---------- date math ---------- */

  // The Nth business day counting from today (today counts as day 1). Sat/Sun skipped.
  // No extra "+1": shipping_days = 1 → today, = 2 → next business day, etc.
  function addBusinessDays(days) {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    if (days <= 0) return d;
    let counted = 0;
    while (true) {
      const wd = d.getDay();
      if (wd !== 0 && wd !== 6) {
        counted += 1;
        if (counted >= days) return d;
      }
      d.setDate(d.getDate() + 1);
    }
  }

  function formatDate(d) {
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }

  /* ---------- variant resolution ---------- */

  function getCurrentVariantId() {
    const atc =
      document.querySelector("button.gp-button-atc") ||
      document.querySelector("button.m-add-to-cart");
    const form = atc && atc.closest("form");
    const scoped = form && form.querySelector('input[name="id"]');
    if (scoped && scoped.value) return String(scoped.value);

    const anyId = document.querySelector(
      'form[action*="/cart/add"] input[name="id"], input[name="id"]',
    );
    if (anyId && anyId.value) return String(anyId.value);

    const v = new URL(window.location.href).searchParams.get("variant");
    if (v) return String(v);

    return DATA.initialVariantId != null ? String(DATA.initialVariantId) : null;
  }

  /* ---------- stock data from the editor ---------- */

  /* A "Stock dropdown" block can supply the stock figures itself, so a product can
     be marked in stock and given an arrival date without touching the variant
     metafields. Absent the block - or with its source left on "metafields" - this
     is inert and every product keeps reading the metafields as before. */
  const DATA_OVERRIDE = (function readDataOverride() {
    const node = document.getElementById("dyn-stock-data-override");
    if (!node) return null;
    let parsed;
    try {
      parsed = JSON.parse(node.textContent);
    } catch (err) {
      return null;
    }
    return parsed && parsed.source === "settings" ? parsed : null;
  })();

  /* Returns the variant record the state should be computed from: the metafield
     record, or a copy of it with the editor's figures laid over the top. Shopify's
     own `available` is never overridden - nothing sold out becomes buyable here. */
  function stockRecord(variantId) {
    const v = VARIANTS[variantId];
    if (!v || !DATA_OVERRIDE) return v;

    const per = (DATA_OVERRIDE.variants || {})[variantId];
    const inStock = per ? per.inStock === true : DATA_OVERRIDE.inStock === true;
    const days = per ? per.shippingDays : DATA_OVERRIDE.shippingDays;

    return {
      available: v.available,
      in_stock: inStock,
      // The editor expresses waiting time one way only, so the metafield's
      // custom_shipping_date - which would otherwise force pre-order - is dropped.
      custom_shipping_date: null,
      shipping_days: days,
    };
  }

  /* ---------- state computation ---------- */

  function stateFor(variantId) {
    const v = stockRecord(variantId);
    if (!v) return null;

    // Shopify availability outranks the metafields — a sold-out variant can't be
    // pre-ordered, so no date is computed and the button is disabled.
    // atcLabel null = leave the button text alone: the native theme already writes
    // "Sold Out" and GemPages "Out of stock", and those are the correct labels here.
    // Our only job is to stop overwriting them with a pre-order invitation.
    if (v.available === false) {
      return {
        status: "out",
        text: CFG.unavailable,
        date: null,
        badge: CFG.unavailableBadge,
        atcLabel: null,
        disabled: true,
      };
    }

    const customDays = parseInt(v.custom_shipping_date, 10);
    const hasCustom = !isNaN(customDays) && customDays > 0;
    // custom_shipping_date overrides in_stock and forces pre-order behaviour.
    const inStock = hasCustom ? false : v.in_stock === true;
    let dateStr = null;
    const fallbackDays = parseInt(v.shipping_days, 10);
    const days = hasCustom ? customDays : fallbackDays;
    if (!isNaN(days) && days > 0)
      dateStr = formatDate(addBusinessDays(days + 1));
    // A date typed in the editor is printed as written - no maths, no weekend
    // skipping - so a merchant can state a date the calculation would not produce.
    if (DATA_OVERRIDE && DATA_OVERRIDE.arrivalDate)
      dateStr = DATA_OVERRIDE.arrivalDate;

    let text;
    if (inStock)
      text = dateStr ? CFG.inStockPrefix + " " + dateStr : CFG.inStockNoDate;
    else
      text = dateStr ? CFG.preorderPrefix + " " + dateStr : CFG.preorderNoDate;

    return {
      status: inStock ? "in" : "pre",
      text: text,
      date: dateStr,
      badge: inStock ? CFG.inStockBadge : CFG.preorderBadge,
      atcLabel: inStock ? CFG.addToCart : CFG.preorderButton,
      disabled: false,
    };
  }

  // Finds the date inside GemPages' own stock copy so it can be emboldened like ours.
  // We do not compose that string, so the date has to be located rather than known.
  //
  // Two strategies, best first:
  //   1. GemPages renders the date in its own `.estimate-date-main` span — exact, no
  //      guessing, and it survives any wording or format change the merchant makes.
  //   2. Otherwise split on the LAST colon: the copy is written "<prefix>: <date>"
  //      (e.g. "Order today — ships: 22 Mar"). Last, not first, so a prefix that itself
  //      contains a colon still yields the trailing date.
  //
  // Returns null unless the result sits at the END of the message — paintDropdown
  // embolden by slicing the tail, so a date found anywhere else would corrupt the text.
  function dateWithin(container, fullText) {
    if (!container || !fullText) return null;

    const marked = container.querySelector(".estimate-date-main");
    if (marked) {
      const markedText = marked.textContent.replace(/\s+/g, " ").trim();
      if (markedText && fullText.endsWith(markedText)) return markedText;
    }

    const colon = fullText.lastIndexOf(":");
    if (colon > -1 && colon < fullText.length - 1) {
      const tail = fullText.slice(colon + 1).trim();
      if (tail) return tail;
    }
    return null;
  }

  // Products without the custom data still get the dropdown (presentation is not gated),
  // so they need something true to show. Preference order: whatever stock copy the page
  // already renders (GemPages writes its own estimate-date row), then Shopify's own
  // availability. `atcLabel: null` means we own no button text here — without the custom
  // data we have no basis to relabel it, and the theme's own label is already correct.
  function fallbackStateFor(variantId) {
    const v = VARIANTS[variantId];
    if (v && v.available === false) {
      return {
        status: "out",
        text: CFG.unavailable,
        date: null,
        badge: CFG.unavailableBadge,
        atcLabel: null,
        disabled: false,
      };
    }

    const source = document.querySelector(".estimate-date-container");
    const sourceDot = document.querySelector(".pulsating-dot");

    // Nothing to mirror and no custom data. Rather than guess at availability —
    // which is how socks ended up advertising a pre-order it does not have — fall
    // back to a fixed line that makes no claim about stock either way. Clearing
    // the string in the locale file turns this off again and restores silence.
    if (!source && !sourceDot) {
      if (!CFG.noDataText) return null;
      return {
        status: "pre",
        text: CFG.noDataText,
        date: null,
        badge: CFG.preorderBadge,
        atcLabel: null,
        disabled: false,
      };
    }

    const sourceText = source
      ? source.textContent.replace(/\s+/g, " ").trim()
      : "";
    const sourceDate = dateWithin(source, sourceText);
    const dotColor = sourceDot
      ? window.getComputedStyle(sourceDot).backgroundColor
      : "";
    // GemPages' own greens, both the stock theme's and this store's override.
    const isGreen = /(?:0\s*,\s*198\s*,\s*94|150\s*,\s*204\s*,\s*41)/.test(
      dotColor,
    );

    return {
      status: isGreen ? "in" : "pre",
      text: sourceText || (isGreen ? CFG.inStockNoDate : CFG.preorderNoDate),
      date: sourceDate,
      badge: isGreen ? CFG.inStockBadge : CFG.preorderBadge,
      atcLabel: null,
      disabled: false,
    };
  }

  // Resolves what the row actually shows. `text`/`date` stay the raw stock state (the ATC
  // and drift logic key off them); `title`/`titleDate` are what gets painted, and
  // `details` is the copy revealed when the row is expanded.
  function finalize(state) {
    if (!state) return state;
    state.title = state.text;
    state.titleDate = state.date;
    // In stock and pre-order answer different questions, so they get different
    // copy. Falls back to the shared string when the in-stock one is absent, so
    // an older locale file still renders something rather than nothing.
    state.details =
      (state.status === "in"
        ? CFG.inStockDetails || CFG.details
        : CFG.details) || "";
    return state;
  }

  /* ---------- painters ---------- */

  const DOT_CLASSES = {
    in: "dyn-dot--in",
    pre: "dyn-dot--pre",
    out: "dyn-dot--out",
  };

  function paintDot(dot, status) {
    dot.classList.remove("dyn-dot--in", "dyn-dot--pre", "dyn-dot--out");
    dot.classList.add(DOT_CLASSES[status]);
  }

  function createDropdown() {
    const accordion = document.createElement("collapsible-tab");
    // NOTE: deliberately NOT using the theme's `m-collapsible*` classes. `collapsible-tab`
    // only needs [data-trigger] / [data-content]; the classes are cosmetic and drag in
    // declarations that fight this design — `.m-collapsible--button` forces display:flex,
    // justify-content:space-between and padding-top/bottom:16px (longhands, so they beat
    // our padding shorthand), and `.m-collapsible--content__inner` adds padding:16px 0.
    accordion.className = "dyn-stock-dropdown no-js-hidden";
    accordion.setAttribute("data-dyn-stock-dropdown", "");
    // one-open=false: collapsible-tab closes every OTHER open tab by default, which
    // would slam the product description/FAQ accordions shut on a stock-row click.
    accordion.setAttribute("data-one-open", "false");
    accordion.innerHTML =
      '<button type="button" class="dyn-stock-dropdown__summary" data-trigger>' +
      '<span class="dyn-stock-dropdown__dot" aria-hidden="true"></span>' +
      '<span class="dyn-stock-dropdown__message"></span>' +
      '<span class="dyn-stock-dropdown__badge"></span>' +
      '<span class="dyn-stock-dropdown__chevron" aria-hidden="true">' +
      '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M13.5306 6.53073L8.5306 11.5307C8.46092 11.6007 8.37813 11.6561 8.28696 11.694C8.1958 11.7318 8.09806 11.7513 7.99935 11.7513C7.90064 11.7513 7.8029 11.7318 7.71173 11.694C7.62057 11.6561 7.53778 11.6007 7.4681 11.5307L2.4681 6.53073C2.3272 6.38984 2.24805 6.19874 2.24805 5.99948C2.24805 5.80023 2.3272 5.60913 2.4681 5.46823C2.60899 5.32734 2.80009 5.24818 2.99935 5.24818C3.19861 5.24818 3.3897 5.32734 3.5306 5.46823L7.99997 9.93761L12.4693 5.46761C12.6102 5.32671 12.8013 5.24756 13.0006 5.24756C13.1999 5.24756 13.391 5.32671 13.5318 5.46761C13.6727 5.60851 13.7519 5.7996 13.7519 5.99886C13.7519 6.19812 13.6727 6.38921 13.5318 6.53011L13.5306 6.53073Z" fill="currentColor"/>' +
      "</svg>" +
      "</span>" +
      "</button>" +
      '<div class="dyn-stock-dropdown__body" data-content hidden>' +
      '<div class="dyn-stock-dropdown__body-copy">' +
      '<span class="dyn-stock-dropdown__details"></span>' +
      "</div>" +
      "</div>";
    return accordion;
  }

  function paintDropdown(dropdown, state) {
    dropdown.setAttribute("data-stock-state", state.status);
    paintDot(dropdown.querySelector(".dyn-stock-dropdown__dot"), state.status);

    const message = dropdown.querySelector(".dyn-stock-dropdown__message");
    const title = state.title || "";
    message.textContent = "";
    if (state.titleDate && title.endsWith(state.titleDate)) {
      message.appendChild(
        document.createTextNode(title.slice(0, -state.titleDate.length)),
      );
      const strong = document.createElement("strong");
      strong.textContent = state.titleDate;
      message.appendChild(strong);
    } else {
      message.textContent = title;
    }

    dropdown.querySelector(".dyn-stock-dropdown__badge").textContent =
      state.badge || "";

    const details = dropdown.querySelector(".dyn-stock-dropdown__details");
    if (details.textContent !== (state.details || ""))
      details.textContent = state.details || "";
    // Nothing to reveal → no chevron and no toggle, so the row can't open onto a blank panel.
    dropdown.toggleAttribute("data-no-details", !state.details);
  }

  // GemPages renders its own pink "Few Spots left!" pill as a SEPARATE gp-row, a sibling
  // of the dot/date row — hiding the dot row alone leaves it stranded outside our
  // dropdown, duplicating the badge we already paint. It carries no stable class or id
  // (the uid is regenerated per section), but its pink background is a reliable marker:
  // every one of the 45 gp-sections that has a `.pulsating-dot` has exactly one
  // `--bgc:#FFEAEA` row. Search is scoped by walking up from the dot's own row so a
  // pink pill elsewhere on the page is never touched.
  const GEM_BADGE_BG = "--bgc:#ffeaea";

  // Returns the un-hidden pink pill rows near `sourceRow`, or [] when there are none.
  function findGemBadges(sourceRow) {
    let scope = sourceRow.parentElement;
    for (
      let depth = 0;
      scope && depth < 6;
      depth += 1, scope = scope.parentElement
    ) {
      const hits = [];
      for (const row of scope.querySelectorAll("gp-row")) {
        if (
          row === sourceRow ||
          row.contains(sourceRow) ||
          sourceRow.contains(row)
        )
          continue;
        if (
          (row.getAttribute("style") || "")
            .toLowerCase()
            .indexOf(GEM_BADGE_BG) === -1
        )
          continue;
        hits.push(row);
      }
      if (hits.length) return hits;
    }
    return [];
  }

  function gemSourceRow() {
    // Still found once hidden — display:none elements are queryable.
    const dot = document.querySelector(".pulsating-dot");
    if (!dot) return null;
    return dot.closest("gp-row") || dot.parentElement;
  }

  // GemPages surface. Hide its original dot/date row + badge pill, place our component there.
  // The hiding is re-asserted on EVERY paint, not just on creation: a GemPages re-render
  // replaces those rows with fresh nodes that no longer carry our class.
  /* The pagebuilder surface belongs to footwear only. Everywhere else its own
     dot-and-date row is left exactly as it was - this feature was never meant
     to touch mats and covers, and quietly rewriting their copy is worse than
     doing nothing at all. */
  const GEM_ALLOWED = DATA.gemAllowed !== false;

  function paintDropdownGem(state) {
    if (!GEM_ALLOWED) return false;
    const source = gemSourceRow();
    let dropdown = document.querySelector('[data-dyn-stock-dropdown="gem"]');
    if (!dropdown) {
      if (!source || !source.parentNode) return false;
      dropdown = createDropdown();
      dropdown.setAttribute("data-dyn-stock-dropdown", "gem");
      source.parentNode.insertBefore(dropdown, source);
    }
    if (source) {
      source.classList.add("dyn-stock-source-hidden");
      findGemBadges(source).forEach((row) =>
        row.classList.add("dyn-stock-source-hidden"),
      );
    }
    paintDropdown(dropdown, state);
    return true;
  }

  /* A template can carry its own stock row - the socks PDP has one as a real
     block in the product info, placed deliberately among the other blocks.
     Injecting this above the buy button as well leaves the shopper reading two
     availability lines that disagree, so the page's own row wins. */
  function pageOwnsStockRow() {
    // Deliberately not [data-stock-state]: the dropdown this file creates
    // carries that too, so after its first paint the feature would read its own
    // output as the page's row and silently stop updating the button.
    return !!document.querySelector("[data-page-stock-row]");
  }

  // Native surface — fill the template's own slot, or inject above the ATC button.
  function paintDropdownNative(state) {
    // A "Stock dropdown" block IS this feature, placed deliberately, so it outranks
    // the guard that otherwise leaves a template's own stock row to speak for itself.
    const slot = document.querySelector("[data-dyn-stock-slot]");
    if (!slot && pageOwnsStockRow()) return false;

    /* Without a slot this injects itself above whatever buy button it can find,
       which is right for the footwear PDPs it was written for and an intrusion
       anywhere else - blocking the pagebuilder surface alone just pushed the
       row onto mats through this path instead. A template that wants the
       feature says so with the block. */
    if (!slot && !GEM_ALLOWED) return false;

    let dropdown = document.querySelector('[data-dyn-stock-dropdown="native"]');
    if (!dropdown) {
      // Without a slot, sit above the buy button - right for the pagebuilder PDPs
      // this was written for, which have nowhere to put a block.
      const atc = slot ? null : document.querySelector("button.m-add-to-cart");
      if (!slot && !atc) return false;

      dropdown = createDropdown();
      dropdown.setAttribute("data-dyn-stock-dropdown", "native");
      // collapsible-tab reads `open` when it connects, so set it before insertion.
      if (slot && slot.dataset.stockOpen === "true")
        dropdown.setAttribute("open", "true");

      if (slot) {
        slot.appendChild(dropdown);
      } else {
        const anchor = atc.closest(".main-product__block-buy_buttons") || atc;
        anchor.parentNode.insertBefore(dropdown, anchor);
      }
    }
    paintDropdown(dropdown, state);
    return true;
  }

  /* ---------- control renderer (what is live on `main` today) ---------- */

  // Recolours GemPages' own dot and swaps its estimate-date copy for ours, leaving the
  // row — and its pink "Few Spots left!" pill — in place. This is the A/B baseline.
  function paintControlGem(state) {
    if (!GEM_ALLOWED) return false;
    const dot = document.querySelector(".pulsating-dot");
    if (!dot) return false;
    paintDot(dot, state.status);

    // Target the inner container (a plain div), not the <gp-estimate-date> element.
    const target =
      document.querySelector(".estimate-date-container") ||
      document.querySelector("gp-estimate-date");
    if (target) {
      let span = target.querySelector(".dyn-stock-text");
      if (!span) {
        target.textContent = "";
        span = document.createElement("span");
        span.className = "dyn-stock-text";
        target.appendChild(span);
      }
      if (span.textContent !== state.text) span.textContent = state.text;
    }
    return true;
  }

  // Native surface — inject a plain message row above the ATC button.
  function paintControlNative(state) {
    if (pageOwnsStockRow()) return false;
    const atc = document.querySelector("button.m-add-to-cart");
    if (!atc) return false;

    let msg = document.querySelector(".dyn-stock-msg[data-dyn-native]");
    if (!msg) {
      msg = document.createElement("div");
      msg.className = "dyn-stock-msg";
      msg.setAttribute("data-dyn-native", "");
      msg.innerHTML =
        '<span class="dyn-stock-msg__dot"></span><span class="dyn-stock-msg__text"></span>';
      const anchor = atc.closest(".main-product__block-buy_buttons") || atc;
      anchor.parentNode.insertBefore(msg, anchor);
    }
    paintDot(msg.querySelector(".dyn-stock-msg__dot"), state.status);
    const txt = msg.querySelector(".dyn-stock-msg__text");
    if (txt.textContent !== state.text) txt.textContent = state.text;
    return true;
  }

  /* ---------- renderer switching ---------- */

  // Removes everything either renderer owns, so a group flip leaves nothing stranded:
  // our dropdown, the injected native row, and — critically — the `dyn-stock-source-hidden`
  // class, which is what brings GemPages' original row and pink pill back for control.
  function teardown() {
    document
      .querySelectorAll("[data-dyn-stock-dropdown]")
      .forEach((el) => el.remove());
    document
      .querySelectorAll(".dyn-stock-msg[data-dyn-native]")
      .forEach((el) => el.remove());
    document
      .querySelectorAll(".dyn-stock-source-hidden")
      .forEach((el) => el.classList.remove("dyn-stock-source-hidden"));
  }

  function paintForGroup(state) {
    if (group === DROPDOWN)
      return paintDropdownGem(state) || paintDropdownNative(state);
    // Control must be indistinguishable from today's live site. Today we paint nothing on
    // a product the custom data does not cover, so painting fallback copy here would put
    // a message in front of control shoppers that production never showed them — and the
    // test would be measuring that too, not just the dropdown.
    if (!DATA_ENABLED) return true;
    return paintControlGem(state) || paintControlNative(state);
  }

  // GemPages renders BOTH buttons and shows one at a time: `.gp-button-atc` normally,
  // `.gp-button-sold-out` (already inert) when the variant is sold out. We never write a
  // label to the sold-out one — it is shown only when the variant is unavailable, and its
  // own "Out of stock" is the right text there. We still reconcile `disabled` on both.
  const GP_ATC = "button.gp-button-atc";
  const GP_SOLD_OUT = "button.gp-button-sold-out";

  function setText(span, label) {
    if (span && span.textContent.trim() !== label) span.textContent = label;
  }

  // Only ever removes a `disabled` WE added (tracked via data-dyn-disabled). The native
  // theme's variant-picker disables the button for its own reasons; clearing that here
  // would re-enable genuinely unbuyable variants.
  function setDisabled(btn, disabled) {
    if (disabled) {
      if (!btn.disabled) {
        btn.setAttribute("disabled", "disabled");
        btn.setAttribute("data-dyn-disabled", "");
      }
      btn.classList.add("disabled");
    } else if (btn.hasAttribute("data-dyn-disabled")) {
      btn.removeAttribute("disabled");
      btn.removeAttribute("data-dyn-disabled");
      btn.classList.remove("disabled");
    }
  }

  /* Every add-to-cart button on the page is not this product's. The cart
     drawer's "customers also bought" cards each carry their own, for entirely
     different products, and relabelling those left a shopper looking at
     "Pre-order - Add to Cart" on an item that is in stock. Only the buttons
     that buy the product this page is about should follow its stock state -
     which includes the sticky bar, so this excludes cart surfaces rather than
     naming the main form. */
  const NOT_THIS_PRODUCT =
    ".m-cart-drawer, .m-cart-items, [data-cart-upsell], .recommendations-grid, " +
    ".m-cart-item, #MinimogCartDrawer, .m-cart-page";

  function buysThisProduct(btn) {
    return !btn.closest(NOT_THIS_PRODUCT);
  }

  function paintAtcLabel(state) {
    // atcLabel === null ('out' state) means: touch no button text at all, only the
    // disabled state. Both surfaces already label sold-out variants correctly on
    // their own; the bug was us overwriting that with a pre-order invitation.
    const label = state.atcLabel;
    document.querySelectorAll(GP_ATC + ", " + GP_SOLD_OUT).forEach((btn) => {
      if (!buysThisProduct(btn)) return;
      if (
        label !== null &&
        !pageOwnsStockRow() &&
        !btn.classList.contains("gp-button-sold-out")
      ) {
        setText(btn.querySelector("span.gp-content-product-button"), label);
      }
      setDisabled(btn, state.disabled);
    });
    document.querySelectorAll("button.m-add-to-cart").forEach((btn) => {
      if (!buysThisProduct(btn)) return;
      // A page with its own stock row has already told the shopper where things
      // stand. Relabelling the button from this feature's view of stock on top
      // of that produced a page arguing with itself - "In stock, ships within
      // 24h" above a button offering a pre-order. The row wins; the disabled
      // state still follows real availability.
      if (label !== null && !pageOwnsStockRow()) {
        setText(btn.querySelector(".m-add-to-cart--text"), label);
        if (
          btn.hasAttribute("data-atc-text") &&
          btn.getAttribute("data-atc-text") !== label
        ) {
          btn.setAttribute("data-atc-text", label);
        }
      }
      setDisabled(btn, state.disabled);
    });
  }

  /* ---------- apply + drift detection ---------- */

  function apply() {
    const vid = getCurrentVariantId();
    const state = vid
      ? finalize(DATA_ENABLED ? stateFor(vid) : fallbackStateFor(vid))
      : null;
    if (!state) {
      // Nothing to say for this variant — clear anything a previous one left behind
      // rather than leaving a stale message on screen.
      if (expected) {
        if (observer) observer.disconnect();
        teardown();
        expected = null;
        if (observer)
          observer.observe(document.body, { childList: true, subtree: true });
      }
      return;
    }
    expected = state;

    if (observer) observer.disconnect();
    paintForGroup(state);
    paintAtcLabel(state);
    if (observer)
      observer.observe(document.body, { childList: true, subtree: true });
  }

  // GemPages re-renders can revert our label/text — detect and re-apply.
  function hasDrift() {
    if (!expected) return false;
    // With atcLabel null we own no button text, so there is no label to drift — chasing
    // one would fight the theme's own "Sold Out" forever.
    const label = expected.atcLabel;
    for (const gpBtn of document.querySelectorAll(
      GP_ATC + ", " + GP_SOLD_OUT,
    )) {
      if (label !== null && !gpBtn.classList.contains("gp-button-sold-out")) {
        const gpLabel = gpBtn.querySelector("span.gp-content-product-button");
        if (gpLabel && gpLabel.textContent.trim() !== label) return true;
      }
      // A sold-out variant whose button GemPages re-rendered as enabled.
      if (expected.disabled && !gpBtn.disabled) return true;
    }
    const natBtn = document.querySelector("button.m-add-to-cart");
    if (natBtn) {
      if (label !== null) {
        const natLabel = natBtn.querySelector(".m-add-to-cart--text");
        if (natLabel && natLabel.textContent.trim() !== label) return true;
      }
      if (expected.disabled && !natBtn.disabled) return true;
    }

    if (group !== DROPDOWN) {
      // Control owns GemPages' own row: drift is our dot colour or copy being reverted.
      const controlText = document.querySelector(".dyn-stock-text");
      if (controlText && controlText.textContent !== expected.text) return true;
      const controlDot = document.querySelector(
        ".pulsating-dot, .dyn-stock-msg__dot",
      );
      if (
        controlDot &&
        !controlDot.classList.contains(DOT_CLASSES[expected.status])
      )
        return true;
      return false;
    }

    // GemPages re-rendered its own dot row or badge pill back into view.
    const gemSource = gemSourceRow();
    if (gemSource) {
      if (!gemSource.classList.contains("dyn-stock-source-hidden")) return true;
      if (findGemBadges(gemSource).length) return true;
    }

    const dropdown = document.querySelector(".dyn-stock-dropdown");
    if (!dropdown || dropdown.dataset.stockState !== expected.status)
      return true;
    const dropdownDot = dropdown.querySelector(".dyn-stock-dropdown__dot");
    if (
      !dropdownDot ||
      !dropdownDot.classList.contains(DOT_CLASSES[expected.status])
    )
      return true;
    const dropdownMessage = dropdown.querySelector(
      ".dyn-stock-dropdown__summary .dyn-stock-dropdown__message",
    );
    if (
      !dropdownMessage ||
      dropdownMessage.textContent.trim() !== (expected.title || "").trim()
    )
      return true;
    const dropdownBadge = dropdown.querySelector(".dyn-stock-dropdown__badge");
    if (
      !dropdownBadge ||
      dropdownBadge.textContent.trim() !== (expected.badge || "")
    )
      return true;
    return false;
  }

  /* ---------- scheduling ---------- */

  function tick() {
    tickScheduled = false;

    // IG's bundle is a module in <head> and this file is deferred, so the assignment can
    // arrive after our first paint. Re-resolve every pass and swap renderers if it changed.
    const resolved = resolveGroup();
    if (resolved !== group) {
      group = resolved;
      if (observer) observer.disconnect();
      teardown();
      if (observer)
        observer.observe(document.body, { childList: true, subtree: true });
      apply();
      return;
    }

    const vid = getCurrentVariantId();
    // Without the custom data our copy is READ from the page, so it has to be re-read
    // every pass — GemPages rewrites its own row on variant change and drift detection
    // would otherwise keep comparing against a stale snapshot.
    if (!DATA_ENABLED) {
      lastVid = vid;
      apply();
      return;
    }
    if (vid !== lastVid) {
      lastVid = vid;
      apply();
      return;
    }
    if (hasDrift()) apply();
  }

  function scheduleTick() {
    if (tickScheduled) return;
    tickScheduled = true;
    setTimeout(tick, 120);
  }

  /* ---------- wiring ---------- */

  function init() {
    // Variant changes: size/colour controls on both surfaces bubble `change` to document.
    document.addEventListener("change", scheduleTick, true);
    // URL-driven variant changes (back/forward, gp updates).
    window.addEventListener("popstate", scheduleTick);

    // Safety net for GemPages label/text re-renders.
    observer = new MutationObserver(function () {
      if (hasDrift() && expected) {
        // GemPages re-rendered and overwrote our state (common during variant
        // switches). Re-assert immediately so the old template text never flashes.
        // Disconnect first so our own writes don't re-trigger the observer.
        observer.disconnect();
        paintForGroup(expected);
        paintAtcLabel(expected);
        observer.observe(document.body, { childList: true, subtree: true });
      }
      if (!tickScheduled) scheduleTick();
    });

    // Initial paint (also starts the observer) + retries for late GemPages hydration.
    apply();
    lastVid = getCurrentVariantId();
    [300, 800, 1500, 2500].forEach((t) => setTimeout(scheduleTick, t));

    // Ensure the observer runs even if the first apply() bailed (no state yet).
    observer.observe(document.body, { childList: true, subtree: true });

    // Group changes are owned by window.pdpAb (see assets/pdp-ab-group.js). Subscribe so
    // an Intelligems assignment arriving after our first paint swaps the renderer, and
    // keep the tick's own refresh() as a backstop if IG only sets the variable.
    if (window.pdpAb && typeof window.pdpAb.onChange === "function") {
      window.pdpAb.onChange(scheduleTick);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
